// Rewards in the Capital. Each deal is real game logic: it reads the real state (rice counted this season,
// your honor rating, your treasury), moves real things, and writes a ledger line with the real numbers.
// Coin goes into the trip ledger (pending until you're home). Every deal is capped per trip and on a
// real-time cooldown that survives reloads.

import { DealConfig } from '../config/capital.js'
import { RiceValue } from '../config/balance.js'
import * as season from './seasonManager.js'
import { getHonor } from './honor.js'
import { credit, getTrip, isOnTrip } from './capitalTrip.js'
import { admit, isAdmitted } from './capitalGuards.js'
import { PalaceConfig } from '../config/capital.js'
import { formatCoins } from '../ui/format.js'

const KEY = 'mao_capital_deals_v1'
const load = () => { try { return JSON.parse(localStorage.getItem(KEY)) || {} } catch { return {} } }
const stamp = (id) => { try { const d = load(); d[id] = Date.now(); localStorage.setItem(KEY, JSON.stringify(d)) } catch { /* ignore */ } }
const cooldownLeft = (id, kind) => Math.max(0, (load()[id] ?? 0) + DealConfig.cooldownMs[kind] - Date.now())
const mins = (ms) => Math.max(1, Math.ceil(ms / 60_000))

// Per-trip counters live on the trip object so a new trip starts fresh.
const tripBag = () => { const t = getTrip(); if (!t) return null; return (t.deals ??= { riceSold: 0, pouches: 0 }) }

// What can the player do at this landmark right now? → { id, label, enabled, run() } | null
export function actionAt(poi) {
  if (!isOnTrip() || !poi) return null
  const bag = tripBag()
  const C = DealConfig

  if (poi.id === 'market') {
    const have = season.getHarvestProjection()
    const room = C.rice.capUnits - bag.riceSold
    const cd = cooldownLeft('rice', 'rice')
    const unit = Math.round(RiceValue * C.rice.priceMult)
    return {
      id: 'rice', label: 'Sell rice to the Imperial Granary',
      note: cd ? `Granary is stocked — back in ${mins(cd)} min` : `${have} rice ready · ${formatCoins(unit)} each`,
      enabled: !cd,
      run() {
        if (cd) return `The Granary clerk waves you off — stocked for ${mins(cd)} more min.`
        const n = Math.min(have, room)
        if (n < 1) return have < 1
          ? `The clerk finds no rice in your count this season (${have}). Nothing sold.`
          : `The Granary will take no more rice from you this visit (${C.rice.capUnits} units).`
        const sold = season.takeRice(n)
        if (!sold) return 'No rice was sold.'
        bag.riceSold += sold
        stamp('rice')
        credit(sold * unit, `Sold ${sold} rice to the Imperial Granary at ${formatCoins(unit)} each`, 'deal:rice')
        return `Sold ${sold} rice for ${formatCoins(sold * unit)}.`
      },
    }
  }

  if (poi.id === 'palace_gate') {
    const ok = isAdmitted()
    return {
      id: 'admission', label: 'Pay the gate fee to enter the palace',
      note: ok ? 'You have leave to enter the courtyard' : `${formatCoins(PalaceConfig.admission)} · guards fine trespassers ${formatCoins(PalaceConfig.fine)}`,
      enabled: !ok,
      run() { return admit() ? `The guards wave you through. ${formatCoins(PalaceConfig.admission)} paid.` : 'The guards already know you — go in.' },
    }
  }

  if (poi.id === 'audience') {
    const h = getHonor()
    const cd = cooldownLeft('stipend', 'stipend')
    return {
      id: 'stipend', label: 'Petition the Emperor’s stipend',
      note: cd ? `Audience closed — back in ${mins(cd)} min` : `Your honor: ${h.toFixed(2)} (need ${C.stipend.minHonor})`,
      enabled: !cd,
      run() {
        if (cd) return `The chamberlain says the Emperor has granted you audience already — return in ${mins(cd)} min.`
        if (h < C.stipend.minHonor) {
          stamp('stipend')
          return `The Emperor regards your honor of ${h.toFixed(2)} and finds it short of ${C.stipend.minHonor}. No stipend.`
        }
        const amt = Math.min(C.stipend.cap, Math.round((h - 5) * C.stipend.perPoint))
        stamp('stipend')
        credit(amt, `Imperial stipend for your honor of ${h.toFixed(2)}`, 'deal:stipend')
        return `The Emperor grants a stipend of ${formatCoins(amt)}.`
      },
    }
  }

  if (C.pouch.spots.includes(poi.id)) {
    const id = `pouch:${poi.id}`
    const cd = cooldownLeft(id, 'pouch')
    if (cd || bag.pouches >= C.pouch.perTrip) return null // nothing to pick up — and we don't pretend there is
    return {
      id, label: `Search the ${poi.name}`, note: 'Something glints in the grass…', enabled: true,
      run() {
        const w = season.getWealth()
        const amt = Math.max(C.pouch.min, Math.min(C.pouch.max, Math.round(w * C.pouch.wealthFrac)))
        bag.pouches++
        stamp(id)
        credit(amt, `Found a coin pouch at the ${poi.name}`, 'deal:pouch')
        return `A pouch with ${formatCoins(amt)}!`
      },
    }
  }
  return null
}
